/**
 * TURN CC · 22 — CalendarKanban module (Dashboard signature block).
 *
 * Toggle between Calendar / Kanban / Both views. Persists selection in
 * localStorage under `hm_dashboard_view`. Fetches events for the current
 * visible month + a padding buffer so nav across months is instant.
 * Corporate events past/future are always fetched within the ±90 days
 * window so multi-day bands stretch correctly.
 */
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Columns3, LayoutDashboard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import CalendarFilters, { loadFilters } from "./CalendarFilters";
import CalendarMonth from "./CalendarMonth";
import KanbanBoard from "./KanbanBoard";
import EventDetailsPanel from "./EventDetailsPanel";
import UpcomingEventsStrip from "./UpcomingEventsStrip";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";

const VIEW_KEY = "hm_dashboard_view";
const VIEWS = ["calendar", "kanban", "both"];

function loadView() {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (VIEWS.includes(v)) return v;
  } catch { /* ignore */ }
  // Sensible default: `both` desktop, `calendar` mobile.
  if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
    return "calendar";
  }
  return "both";
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarKanban() {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState(loadView);
  const [nav, setNav] = useState({
    year: today.getFullYear(),
    monthIdx: today.getMonth(),
  });
  const [filters, setFilters] = useState(loadFilters);
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* ignore */ }
  }, [view]);

  // Fetch window: current month ±1 to preload prev/next navigation.
  const { from, to } = useMemo(() => {
    const first = new Date(nav.year, nav.monthIdx, 1);
    const from = new Date(first);
    from.setDate(1);
    from.setMonth(from.getMonth() - 1);
    const to = new Date(first);
    to.setMonth(to.getMonth() + 2);
    to.setDate(0); // last day of next month
    return { from: ymd(from), to: ymd(to) };
  }, [nav]);

  const { data, isFetching, isPending } = useCalendarEvents({ from, to, types: filters });
  const events = data?.events || [];

  const openEvent = (evt) => {
    setSelected(evt);
    setPanelOpen(true);
  };

  return (
    <div
      data-testid="calendar-kanban"
      className="hm-surface p-4 md:p-5 flex flex-col gap-4"
    >
      {/* Top bar: title + view toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="h-4 w-4 text-hm-accent" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
            Calendar
            <span className="mx-1.5 text-slate-300 dark:text-slate-600 font-normal">·</span>
            Pipeline
          </span>
          {isFetching && !isPending && (
            <Loader2
              data-testid="calendar-fetching-indicator"
              className="h-3 w-3 text-slate-400 animate-spin"
            />
          )}
        </div>
        <div
          data-testid="dashboard-view-toggle"
          className="inline-flex items-center gap-0.5 p-1 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10"
        >
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`dashboard-view-${v}`}
              data-active={view === v}
              onClick={() => setView(v)}
              className={
                "px-3 py-1 rounded-md text-xs font-medium capitalize transition-all inline-flex items-center gap-1.5 " +
                (view === v
                  ? "bg-white dark:bg-white/10 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100")
              }
            >
              {v === "calendar" && <CalendarDays className="h-3.5 w-3.5" />}
              {v === "kanban" && <Columns3 className="h-3.5 w-3.5" />}
              {v === "both" && <LayoutDashboard className="h-3.5 w-3.5" />}
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Filters (only shown when calendar is visible) */}
      {(view === "calendar" || view === "both") && (
        <CalendarFilters value={filters} onChange={setFilters} />
      )}

      {/* Upcoming corporate events strip (calendar view only) */}
      {(view === "calendar" || view === "both") && (
        <UpcomingEventsStrip onEventClick={openEvent} />
      )}

      {/* Calendar section */}
      {(view === "calendar" || view === "both") && (
        <CalendarMonth
          year={nav.year}
          monthIdx={nav.monthIdx}
          onNav={setNav}
          onEventClick={openEvent}
          events={events}
          isLoading={isPending && !data}
        />
      )}

      {/* Divider */}
      {view === "both" && <div className="h-px bg-hm-border my-1" />}

      {/* Kanban section */}
      {(view === "kanban" || view === "both") && (
        <KanbanBoard onEventClick={openEvent} />
      )}

      <EventDetailsPanel
        event={selected}
        open={panelOpen}
        onOpenChange={setPanelOpen}
      />
    </div>
  );
}
